import { useEffect } from "react";
import { Navigate, Route, Routes, Link, useLocation } from "react-router-dom";
import { AppNavbar } from "./components/AppNavbar";
import { HomePage } from "./pages/HomePage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { AdminServersPage } from "./pages/AdminServersPage";
import { AdminRaidContentPage } from "./pages/AdminRaidContentPage";
import { EncounterDetailPage } from "./pages/EncounterDetailPage";
import { ProgressionPage } from "./pages/ProgressionPage";
import {
    ProgressionBossRankingsPage,
    ProgressionTierRankingsPage,
} from "./pages/ProgressionAllRankingsPage";
import { GuildsPage } from "./pages/GuildsPage";
import { GuildDetailPage } from "./pages/GuildDetailPage";
import { AdminGuildsPage } from "./pages/AdminGuildsPage";
import { CharacterDetailPage } from "./pages/CharacterDetailPage";
import { CharacterBossParsesPage } from "./pages/CharacterBossParsesPage";
import { BossRankingsPage } from "./pages/BossRankingsPage";
import { AllStarsPage } from "./pages/AllStarsPage";
import { RequireAdmin } from "./auth/RequireAdmin";
import { AccountPage } from "./pages/AccountPage";
import { ReportsPage } from "./pages/ReportsPage";
import { ReportDetailPage } from "./pages/ReportDetailPage";
import { ReportEncountersPage } from "./pages/ReportEncountersPage";
import { BonusesPage } from "./pages/BonusesPage";
import { BonusesHistoryPage } from "./pages/BonusesHistoryPage";
import { BonusZonePage } from "./pages/BonusZonePage";
import { BonusZonesPage } from "./pages/BonusZonesPage";
import { AdminBonusesPage } from "./pages/AdminBonusesPage";
import { AdminContentReportsPage } from "./pages/AdminContentReportsPage";
import { AdminBonusZonesPage } from "./pages/AdminBonusZonesPage";
import { SupportPage } from "./pages/SupportPage";
import { ClientPage } from "./pages/ClientPage";
import { TermsPrivacyPage } from "./pages/TermsPrivacyPage";
import { AdminUsersPage } from "./pages/AdminUsersPage";
import { VanikiBrosPage } from "./pages/VanikiBrosPage";
import { NewsArchivePage } from "./pages/NewsArchivePage";
import { NewsPostPage } from "./pages/NewsPostPage";

import { getSiteContext } from "./utils/siteContext";
import ReactGA from 'react-ga4';


const TRACKING_ID = "G-BQMSMH7MH5";
let gaInitialized = false;

function BonusZoneRedirect() {
    const path = window.location.pathname.replace(
        /^\/bonuses/,
        "/resource-hunter",
    );
    return <Navigate to={path} replace />;
}

export function App() {
    const location = useLocation();
    
    useEffect(() => {
        document.title = getSiteContext().brand;
    }, []);

    useEffect(() => {
        if (!gaInitialized) {
            ReactGA.initialize(TRACKING_ID);
            gaInitialized = true;
        }

        ReactGA.send({
            hitType: "pageview",
            page: `${location.pathname}${location.search}`,
            title: document.title,
        });
    }, [location.pathname, location.search]);

    return (
        <div className="app-shell min-vh-100 bg-body text-body d-flex flex-column">
            <AppNavbar />
            <main id="main-content" className="site-main flex-grow-1">
                <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/encounters/:id" element={<EncounterDetailPage />} />
                    <Route path="/guilds" element={<GuildsPage />} />
                    <Route path="/guilds/:guildSlug" element={<GuildDetailPage />} />
                    <Route path="/guilds/:guildSlug/:guildTab" element={<GuildDetailPage />} />
                    <Route path="/reports" element={<ReportsPage />} />
                    <Route path="/reports/:reportId" element={<ReportDetailPage />} />
                    <Route path="/reports/:reportId/encounters" element={<ReportEncountersPage />} />
                    <Route path="/resource-hunter" element={<BonusesPage />} />
                    <Route path="/posts" element={<NewsArchivePage />} />
                    <Route path="/posts/page/:pageNum" element={<NewsArchivePage />} />
                    <Route path="/posts/:date/:title" element={<NewsPostPage />} />
                    <Route
                        path="/resource-hunter/history"
                        element={<BonusesHistoryPage />}
                    />
                    <Route
                        path="/resource-hunter/zones"
                        element={<BonusZonesPage />}
                    />
                    <Route
                        path="/resource-hunter/zones/:zoneSlug"
                        element={<BonusZonePage />}
                    />
                    <Route
                        path="/bonuses"
                        element={<Navigate to="/resource-hunter" replace />}
                    />
                    <Route
                        path="/bonuses/history"
                        element={<Navigate to="/resource-hunter/history" replace />}
                    />
                    <Route
                        path="/bonuses/zones/:zoneSlug"
                        element={<BonusZoneRedirect />}
                    />
                    <Route
                        path="/characters"
                        element={<Navigate to="/rankings" replace />}
                    />
                    <Route path="/characters/:id" element={<CharacterDetailPage />} />
                    <Route
                        path="/characters/:id/bosses/:bossId"
                        element={<CharacterBossParsesPage />}
                    />

                    <Route path="/rankings" element={<ProgressionPage />} />
                    <Route
                        path="/rankings/progression/:expansionSlug"
                        element={<ProgressionTierRankingsPage />}
                    />
                    <Route
                        path="/rankings/progression/:expansionSlug/:bossSlug"
                        element={<ProgressionBossRankingsPage />}
                    />
                    <Route
                        path="/rankings/boss-rankings/:expansionSlug"
                        element={<BossRankingsPage />}
                    />
                    <Route
                        path="/rankings/boss-rankings/:expansionSlug/:bossSlug/:metricSegment"
                        element={<BossRankingsPage />}
                    />
                    <Route
                        path="/rankings/boss-rankings/:expansionSlug/:bossSlug/:metricSegment/:classSlug"
                        element={<BossRankingsPage />}
                    />
                    <Route
                        path="/rankings/boss/:bossId"
                        element={<ProgressionBossRankingsPage />}
                    />
                    <Route
                        path="/rankings/boss/:bossId/damage"
                        element={<BossRankingsPage metricOverride="DAMAGE" />}
                    />
                    <Route
                        path="/rankings/boss/:bossId/damage/:classSlug"
                        element={<BossRankingsPage metricOverride="DAMAGE" />}
                    />
                    <Route
                        path="/rankings/boss/:bossId/healing"
                        element={<BossRankingsPage metricOverride="HEALING" />}
                    />
                    <Route
                        path="/rankings/boss/:bossId/healing/:classSlug"
                        element={<BossRankingsPage metricOverride="HEALING" />}
                    />
                    <Route
                        path="/rankings/allstars/:expansionSlug/:partitionSlug/:bossSlug/:category"
                        element={<AllStarsPage />}
                    />
                    <Route
                        path="/rankings/allstars/:expansionSlug/:partitionSlug/:bossSlug/:category/:classSlug"
                        element={<AllStarsPage />}
                    />
                    <Route
                        path="/rankings/allstars/:expansionSlug/:partitionSlug/:bossSlug/:category/all/:pageNum"
                        element={<AllStarsPage />}
                    />
                    <Route
                        path="/rankings/allstars/:expansionSlug/:partitionSlug/:bossSlug/:category/all/:pageNum/:classSlug"
                        element={<AllStarsPage />}
                    />
                    <Route path="/account" element={<AccountPage />} />
                    <Route
                        path="/admin/servers"
                        element={
                            <RequireAdmin>
                                <AdminServersPage />
                            </RequireAdmin>
                        }
                    />
                    <Route
                        path="/admin/raid-content"
                        element={
                            <RequireAdmin>
                                <AdminRaidContentPage />
                            </RequireAdmin>
                        }
                    />
                    <Route
                        path="/admin/guilds"
                        element={
                            <RequireAdmin>
                                <AdminGuildsPage />
                            </RequireAdmin>
                        }
                    />
                    <Route
                        path="/admin/bonuses"
                        element={
                            <RequireAdmin>
                                <AdminBonusesPage />
                            </RequireAdmin>
                        }
                    />
                    <Route
                        path="/admin/content-reports"
                        element={
                            <RequireAdmin>
                                <AdminContentReportsPage />
                            </RequireAdmin>
                        }
                    />
                    <Route
                        path="/admin/users"
                        element={
                            <RequireAdmin>
                                <AdminUsersPage />
                            </RequireAdmin>
                        }
                    />
                    <Route
                        path="/admin/bonus-zones"
                        element={
                            <RequireAdmin>
                                <AdminBonusZonesPage />
                            </RequireAdmin>
                        }
                    />
                    <Route path="/support" element={<SupportPage />} />
                    <Route path='/terms-privacy' element={<TermsPrivacyPage />} />
                    <Route path="/client" element={<ClientPage />} />
                    <Route path="/vaniki-bros" element={<VanikiBrosPage />} />
                    <Route path="*" element={<NotFoundPage />} />
                </Routes>
            </main>
            <footer className="site-footer">
                <div className="content-wrapper py-3 small text-body-secondary">
                    <div className="d-flex flex-column flex-md-row justify-content-between">
                        <p>v2.1.1 - May 2026</p>
                        <div className="d-flex flex-row gap-3">
                            <p>© 2026  <Link to='/terms-privacy'>Terms & Privacy</Link></p>
                            <a href='https://discord.gg/hm9gGqnjK9' target='_blank'>Discord</a>
                        </div>
                        
                        <div className="d-flex flex-row gap-3">
                            <a href='https://ko-fi.com/winter_xo' className='btn btn-outline-secondary' target='_blank'>Ko-Fi</a>
                            <a href='https://www.patreon.com/c/winter_xo' className='btn btn-outline-secondary' target='_blank'>Patreon</a>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}
